import { CmdInterpreter } from 'BMapsCmds';
import { App } from 'BMapsSrc/BMapsApp';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { wordsToCommands } from 'BMapsUtil/speech_utils';

/**
 * @typedef {{ resSpec, cmpdIndex, selectString }} EntityRef
 */

/**
 * @description Takes a string input, converts it to commands using the commands API,
 * applies the resulting command representations, and publishes human friendly descriptions.
 * @param {string} input
 * @returns {Promise<void>}
 */
export async function applyWordsAsCommands(input) {
    if (!input || input.length === 0) {
        return;
    }

    const context = {};
    const focusCmpd = App.Workspace.getActiveCompound();
    if (focusCmpd) {
        context.focusCompound = { cmpdIndex: getIndexForCompound(focusCmpd) };
    }
    const commandArr = await wordsToCommands(input, context);
    EventBroker.publish('commandInput', { inputWords: input });
    const interpretedCmds = await CmdInterpreter.interpretCommand(commandArr);
    EventBroker.publish('commandInput', { interpretedCmds });
}

/**
 * Return the global index for a compound.
 * @param {import('BMapsModel').Compound} compound
 * @returns {number}
 */
export function getIndexForCompound(compound) {
    const loadedCompounds = App.Workspace.getLoadedCompounds();
    const zeroIndexed = loadedCompounds.indexOf(compound);
    return zeroIndexed !== -1 ? zeroIndexed + 1: null;
}

/**
 * Return the compound for a given global index.
 * @param {number} index
 * @returns {import('BMapsModel').Compound}
 */
export function getCompoundForIndex(index) {
    const loadedCompounds = App.Workspace.getLoadedCompounds();
    if (typeof index !== 'number' || index < 1 || index > loadedCompounds.length) {
        console.warn(`Warning: invalid 1-indexed compound index: ${index}. Must be between 1 and ${loadedCompounds.length}.`);
        return null;
    }
    const zeroIndexed = index - 1;
    return loadedCompounds[zeroIndexed] || null;
}

/**
 * Convert an entity reference into displayable version.
 * This returns resSpec and select strings as they are.
 * For cmpd index, it returns: Compound <index> (<cmpd name>)
 * @param {EntityRef | EntityRef[]} entityRef
 * @returns {string}
 */
export function formatLabels(entityRef) {
    if (Array.isArray(entityRef)) {
        return entityRef.map(formatLabels).join(', ');
    }
    if (!entityRef || typeof entityRef !== 'object') return '(unknown)';
    const { cmpdIndex, resSpec, selectString } = entityRef;
    if (cmpdIndex != null) {
        const index = Number(cmpdIndex);
        const cmpd = getCompoundForIndex(index);
        const label = cmpd?.displayName || 'unknown';
        return `Compound ${index} (${label})`;
    }
    if (selectString) return selectString;
    if (resSpec) return resSpec;
    return '(unknown)';
}
